import React, { FunctionComponent } from 'react';
import TimerIcon from 'src/shared/material_icons/image/timer.svg';
import styled from 'styled-components';
import { Icon } from '../icon';
import { T } from './helpers';

const TRIAL_TOTAL_DAYS = 14;

const Container = styled.div<{ $comfortable?: boolean }>`
    display: flex;
    flex-direction: column;
    gap: ${(props) => (props.$comfortable ? '12px' : '6px')};
    padding: ${(props) => (props.$comfortable ? '14px 16px' : '8px 10px')};
    border-radius: 8px;
    background: #fffbf5;
    border: 1px solid #fde5c8;
`;

const TopRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
`;

const Info = styled.div<{ $comfortable?: boolean }>`
    display: flex;
    align-items: center;
    gap: ${(props) => (props.$comfortable ? '10px' : '6px')};
    font-family: 'Inter', sans-serif;
    font-size: ${(props) => (props.$comfortable ? '14px' : '12px')};
    font-weight: ${(props) => (props.$comfortable ? '600' : '500')};
    color: #92400e;
    min-width: 0;
`;

const TrialIcon = styled(Icon)`
    width: 16px;
    height: 16px;
    min-width: 16px;
    flex-shrink: 0;
`;

const DaysBadge = styled.span<{ $urgent: boolean; $comfortable?: boolean }>`
    display: inline-flex;
    align-items: center;
    padding: ${(props) => (props.$comfortable ? '2px 9px' : '1px 7px')};
    border-radius: 10px;
    font-size: ${(props) => (props.$comfortable ? '11px' : '10px')};
    font-weight: 700;
    background: ${(props) => (props.$urgent ? '#fef2f2' : '#fff')};
    color: ${(props) => (props.$urgent ? '#dc2626' : '#e8650a')};
    border: 1px solid ${(props) => (props.$urgent ? '#fecaca' : '#fde5c8')};
    flex-shrink: 0;
`;

const UpgradeButton = styled.a`
    display: inline-flex;
    align-items: center;
    padding: 4px 12px;
    border-radius: 6px;
    background: #e8650a;
    color: #fff;
    font-family: 'Inter', sans-serif;
    font-size: 11px;
    font-weight: 600;
    text-decoration: none;
    cursor: pointer;
    white-space: nowrap;
    transition: background 0.15s ease;
    flex-shrink: 0;

    &:hover {
        background: #c75508;
    }
`;

const ProgressTrack = styled.div<{ $comfortable?: boolean }>`
    width: 100%;
    height: ${(props) => (props.$comfortable ? '5px' : '3px')};
    border-radius: 3px;
    background: #fde5c8;
    overflow: hidden;
`;

const ProgressBar = styled.div<{ $percent: number }>`
    height: 100%;
    border-radius: 2px;
    background: ${(props) => (props.$percent >= 75 ? '#dc2626' : props.$percent >= 50 ? '#ea580c' : '#e8650a')};
    width: ${(props) => props.$percent}%;
    transition: width 0.3s ease;
`;

interface TrialBannerProps {
    daysOfTrial: number | undefined;
    isVisibleSubscribePlan: boolean;
    // Custom action(s) rendered on the right of the banner (e.g. page-level Upgrade / Cancel buttons).
    // When provided, they replace the default href-based Upgrade button used in the sidebar.
    actions?: React.ReactNode;
    // Roomier padding/typography for wider contexts (e.g. the subscription page). Defaults to the compact
    // sizing used in the narrow sidebar.
    comfortable?: boolean;
}

const TrialBanner: FunctionComponent<TrialBannerProps> = ({ daysOfTrial, isVisibleSubscribePlan, actions, comfortable }) => {
    const days = daysOfTrial ?? 0;
    const percent = Math.max(0, Math.min(100, ((TRIAL_TOTAL_DAYS - days) / TRIAL_TOTAL_DAYS) * 100));
    const urgent = days <= 3;

    return (
        <Container $comfortable={comfortable}>
            <TopRow>
                <Info $comfortable={comfortable}>
                    <TrialIcon aria-hidden='true'>
                        <TimerIcon />
                    </TrialIcon>
                    {T._('Free Trial', 'Admin')}
                    <DaysBadge $urgent={urgent} $comfortable={comfortable}>
                        {days} {T._('days left', 'Admin')}
                    </DaysBadge>
                </Info>
                {actions ?? (isVisibleSubscribePlan && <UpgradeButton href='/Admin/Seller/UpgradeTrialSubscription'>{T._('Upgrade', 'Admin')}</UpgradeButton>)}
            </TopRow>
            <ProgressTrack $comfortable={comfortable}>
                <ProgressBar $percent={percent} />
            </ProgressTrack>
        </Container>
    );
};

export default TrialBanner;
